import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { appendEmployeeNames, removeEmployeeNames, clearEmployeeNames } from './employeeNamesActions';
import useEmployeeNamesInfoReceiver from './employeeNameInfoReceiver';
import moment from 'moment';

const NAME_EXPIRES_IN_SECS = 60 * 60 * 24 * 5; // 5 days

const isEmployeeNamesExpired = (data: Dictionary<any>) => {
  if (!data?.updatedAt) {
    return true;
  }

  const creationDate = moment(data.updatedAt);
  const expiryDate = creationDate.add(NAME_EXPIRES_IN_SECS, 'seconds');
  const currentDate = moment();

  return currentDate > expiryDate;
};

const useEmployeeNamesService = () => {
  const { collectEmployeeNamesData } = useEmployeeNamesInfoReceiver();

  const dispatch = useDispatch();
  const employeeNames = useSelector((state: { employeeNames: any }) => state.employeeNames, shallowEqual);

  const setEmployeeNamesToStore = (employeeNamesData: any) => dispatch(appendEmployeeNames(employeeNamesData));

  const removeEmployeeNamesFromStore = (aliases: string[]) => dispatch(removeEmployeeNames(aliases));

  const getEmployeeNamesByAliases = async (aliases: string[], isHandleError = false) => {
    const missed = aliases.filter(alias => !employeeNames[alias] || isEmployeeNamesExpired(employeeNames[alias]));

    let missedEmployeeNames: Dictionary<any> = {};
    if (missed.length) {
      missedEmployeeNames = await collectEmployeeNamesData(missed, isHandleError);
    }

    setEmployeeNamesToStore(missedEmployeeNames);

    return (
      aliases
        .map(alias => employeeNames[alias] || missedEmployeeNames[alias])
        .map(a => Object.freeze(a))
        .filter(name => name !== undefined) || []
    );
  };

  const getEmployeeNameDataById = async (employeeAlias?: string, isHandleError = false) => {
    if (!employeeAlias) {
      return Promise.resolve({});
    }

    const result = await getEmployeeNamesByAliases([employeeAlias], isHandleError);
    return result[0];
  };

  const getEmployeeAvatarDataById = async (employeeAlias?: string) => {
    if (!employeeAlias) {
      return Promise.resolve(null);
    }

    const employee = employeeNames[employeeAlias];
    if (!employee || isEmployeeNamesExpired(employee)) {
      const result = await getEmployeeNamesByAliases([employeeAlias]);
      const updatedEmployee = result[0];
      return updatedEmployee ? { alias: employeeAlias, mainPhotoId: updatedEmployee.mainPhotoId } : null;
    }

    return { alias: employeeAlias, mainPhotoId: employee.mainPhotoId };
  };

  const getEmployeeWorkingEmailDataById = async (employeeAlias?: string) => {
    if (!employeeAlias) {
      return Promise.resolve(null);
    }

    const employee = employeeNames[employeeAlias];
    if (!employee || isEmployeeNamesExpired(employee)) {
      const result = await getEmployeeNamesByAliases([employeeAlias]);
      const updatedEmployee = result[0];
      return updatedEmployee ? { alias: employeeAlias, workingEmail: updatedEmployee.workingEmail } : null;
    }

    return { alias: employeeAlias, workingEmail: employee.workingEmail };
  };

  const clearEmployeeNamesFromStore = () => dispatch(clearEmployeeNames());

  return {
    getEmployeeNamesByAliases,
    getEmployeeNameDataById,
    getEmployeeAvatarDataById,
    getEmployeeWorkingEmailDataById,
    setEmployeeNamesToStore,
    removeEmployeeNamesFromStore,
    clearEmployeeNamesFromStore,
  };
};

export default useEmployeeNamesService;
